import { Link, useLocation, useNavigate } from 'react-router-dom';

import { ArrowLeft, Bell, LogOut, ScanSearch, Settings, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ThemeToggle } from '@/components/ThemeToggle';
import { UserAvatar } from '@/components/UserAvatar';
import { signOut, useSession } from '@/api/auth';
import { useIsAdmin } from '@/api/hooks';
import { usePermissions } from '@/api/permissions';
import { can } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { useBackTarget } from '@/lib/navHistory';
import { useNotifications } from '@/contexts/NotificationContext';
import { ROUTES, ticketPath } from '@/lib/routes';
import { useClientScope } from '@/contexts/ClientScopeContext';
import { ClientPicker } from '@/components/shell/ClientPicker';
import { CosmosWordmark } from '@/components/CosmosMark';
import { PageSearch } from '@/components/PageSearch';
import { RecordFind } from '@/components/shell/RecordFind';
import { BookmarkButton } from '@/components/shell/Bookmarks';
import { NewMenu } from '@/components/shell/NewMenu';

/**
 * Top header — logo, page search, notifications, theme, user. Module navigation lives in the
 * left sidebar (components/shell/Sidebar). Next to the logo is the global client picker
 * (`ClientPicker`, dev/planning/general.md §4.9 Phase B). On mobile a menu button opens the sidebar as a sheet;
 * the PSA BottomNav stays.
 */
export function AppHeader() {
  const loc = useLocation();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const { newTickets, notices, readNotice, dismissNotification, clearNotifications } = useNotifications();
  const unread = newTickets.length + notices.length;

  const handleSignOut = async () => {
    await signOut();
    toast.success('Signed out');
    window.location.href = ROUTES.login;
  };

  const userName = (session?.user as { name?: string; email?: string } | undefined)?.name
                ?? session?.user?.email
                ?? '';
  const userEmail = session?.user?.email ?? null;
  const { data: isAdmin } = useIsAdmin();
  const perms = usePermissions();
  const isSettings = loc.pathname.startsWith(ROUTES.account.root) || loc.pathname.startsWith(ROUTES.admin.root);

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-card/95 pt-[env(safe-area-inset-top)] backdrop-blur supports-[backdrop-filter]:bg-card/80">
      <div className="flex h-14 w-full items-center justify-between gap-4 px-4">
        {/* Left: logo. Navigation is the sidebar on desktop and the tab bar + More sheet on phones (BottomNav). */}
        <div className="flex min-w-0 items-center gap-2">
          <Link to={ROUTES.home} aria-label="Cosmos home" className="shrink-0">
            <CosmosWordmark size={28} />
          </Link>
          <HeaderBack />
          {/* Global client scope (CIPP/Glass put theirs here too). */}
          <div className="ml-4 hidden md:block"><ClientPicker /></div>
        </div>

        {/* Right: utilities — page search first (desktop only) */}
        <div className="flex items-center gap-2">
          <BookmarkButton />
          {can(perms, 'insights.find') && <RecordFind />}
          {/* Phones: record search (Find) as a header icon; the Search tab is the ticket + opportunity search. */}
          {can(perms, 'insights.find') && (
            <Button variant="ghost" size="icon" asChild className="md:hidden" title="Find a device, person or ticket" aria-label="Find">
              <Link to={ROUTES.insights.find}><ScanSearch className="h-4 w-4"/></Link>
            </Button>
          )}
          <PageSearch />
          <NewMenu />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="relative inline-flex">
                <Button variant="ghost" size="icon" aria-label="Notifications">
                  <Bell className="h-4 w-4"/>
                </Button>
                {unread > 0 && (
                  <span className="pointer-events-none absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-0.5 text-[10px] font-bold leading-none text-white">
                    {unread > 9 ? '9+' : unread}
                  </span>
                )}
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              {/* Personal notifications (quotes opened / accepted / declined / expiring), stored until read. */}
              {notices.length > 0 && (
                <div className="border-b border-divider">
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-xs font-semibold text-foreground">For you</span>
                    <button onClick={() => readNotice(null)} className="text-[11px] text-muted-foreground hover:text-foreground">Mark all read</button>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {notices.map((n) => (
                      <DropdownMenuItem key={n.id} onSelect={() => { readNotice(n.id); if (n.link) navigate(n.link); }}
                        className="flex flex-col items-start gap-0.5 py-2 pl-3 pr-3">
                        <span className="line-clamp-2 text-xs font-medium text-foreground">{n.title}</span>
                        {n.body && <span className="line-clamp-2 text-[11px] text-muted-foreground">{n.body}</span>}
                        <span className="text-[10.5px] text-muted-foreground/80">{new Date(n.createdAt).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                      </DropdownMenuItem>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between border-b border-divider px-3 py-2">
                <span className="text-xs font-semibold text-foreground">New tickets this session</span>
                {newTickets.length > 0 && (
                  <button
                    onClick={clearNotifications}
                    className="text-[11px] text-muted-foreground hover:text-foreground"
                  >
                    Clear all
                  </button>
                )}
              </div>
              {newTickets.length === 0 ? (
                <div className="px-3 py-5 text-center text-xs text-muted-foreground">No new tickets yet</div>
              ) : (
                <>
                  <div className="max-h-64 overflow-y-auto">
                    {newTickets.map((t) => (
                      <div key={t.id} className="group relative">
                        <DropdownMenuItem
                          onSelect={() => { navigate(ticketPath(t.id)); }}
                          // Keyboard: Delete / Backspace dismisses the highlighted ticket.
                          onKeyDown={(e) => { if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); dismissNotification(t.id); } }}
                          className="flex flex-col items-start gap-0.5 py-2 pl-3 pr-9"
                        >
                          <span className="text-[11px] font-medium text-muted-foreground">{t.ticketNumber}</span>
                          <span className="line-clamp-1 text-xs text-foreground">{t.title}</span>
                        </DropdownMenuItem>
                        {/* Dismiss just this one; the menu stays open. Always visible on touch, on hover/focus otherwise. */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); dismissNotification(t.id); }}
                          onPointerDown={(e) => e.stopPropagation()}
                          title="Dismiss"
                          aria-label={`Dismiss ${t.ticketNumber}`}
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground opacity-100 hover:bg-muted hover:text-foreground focus-visible:opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:group-focus-within:opacity-100"
                        >
                          <X className="h-3.5 w-3.5"/>
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-divider px-3 py-2">
                    <button
                      onClick={() => { navigate(ROUTES.psa.tickets.queues); clearNotifications(); }}
                      className="text-[11px] text-brand-strong hover:underline dark:text-brand"
                    >
                      View all in Queues →
                    </button>
                  </div>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Theme, Settings and Sign out live in the More sheet on phones (BottomNav). */}
          <span className="hidden md:inline-flex"><ThemeToggle/></span>

          {/* Settings: personal settings for everyone + every Admin page for admins (Admin left the sidebar). */}
          <Button variant="ghost" size="icon" asChild title="Settings" aria-label="Settings"
            className={cn('hidden md:inline-flex', isSettings && 'bg-muted text-foreground')}>
            <Link to={ROUTES.account.info}><Settings className="h-4 w-4"/></Link>
          </Button>

          {userName && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="hidden items-center gap-2 rounded-md px-2 py-1 transition-colors hover:bg-muted sm:inline-flex focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                >
                  <UserAvatar name={userName} size={26}/>
                  <span className="hidden text-xs text-muted-foreground lg:inline">{userName}</span>
                </button>
              </DropdownMenuTrigger>
              {/* A small user card; everything else lives in Settings. */}
              <DropdownMenuContent align="end" className="w-64 p-0">
                <div className="flex items-center gap-3 px-4 py-3">
                  <UserAvatar name={userName} size={36}/>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">{userName}</div>
                    {userEmail && userEmail !== userName && <div className="truncate text-xs text-muted-foreground">{userEmail}</div>}
                    <div className="mt-0.5 text-[11px] text-muted-foreground">{perms?.roleName ?? (isAdmin ? 'Cosmos admin' : 'Cosmos user')}</div>
                  </div>
                </div>
                <div className="border-t border-divider px-4 py-2">
                  <Link to={ROUTES.account.info} className="text-xs text-muted-foreground hover:text-foreground hover:underline">Settings →</Link>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Button variant="ghost" size="icon" className="hidden md:inline-flex" onClick={handleSignOut} title="Sign out" aria-label="Sign out">
            <LogOut className="h-4 w-4"/>
          </Button>
        </div>
      </div>
      <ScopeChip />
    </header>
  );
}

/**
 * Phones: the client scope is set from More, so show it under the header whenever it's on. A hidden scope would
 * silently filter every list (Mobile.md §5).
 */
function ScopeChip() {
  const { scope, setScope } = useClientScope();
  if (!scope) return null;
  return (
    <div className="flex items-center gap-2 border-t border-brand/20 bg-brand-soft/60 px-4 py-1 text-xs text-brand-strong md:hidden dark:text-brand">
      <span className="min-w-0 flex-1 truncate">Client: <b>{scope.name}</b></span>
      <button type="button" onClick={() => setScope(null)} aria-label="Clear client" className="rounded p-1 active:bg-background/60"><X className="h-3.5 w-3.5" /></button>
    </div>
  );
}

/**
 * Back to the previous Cosmos page (this tab), where a browser's Back would be: the installed app has none
 * (lib/navHistory.ts). Desktop / tablet only (phones have their own Back). Hidden when there's nowhere to go back to.
 */
function HeaderBack() {
  const back = useBackTarget();
  const navigate = useNavigate();
  if (!back) return null;
  const label = `Back to ${back.title ?? 'the previous page'}`;
  return (
    <Button variant="ghost" size="icon" className="ml-2 hidden h-8 w-8 text-muted-foreground hover:text-foreground md:inline-flex"
      onClick={() => navigate(-1)} title={`${label} (Alt+←)`} aria-label={label}>
      <ArrowLeft className="h-4 w-4" />
    </Button>
  );
}
